<template>
  <div class="recoltes-line-chart">
    <div class="chart-header">
      <span class="chart-title">Évolution temporelle des récoltes</span>
      <el-select
        v-if="availableMonths && availableMonths.length > 0"
        v-model="selectedMonth"
        placeholder="Sélectionner un mois"
        size="small"
        class="month-select"
        @change="onMonthChange"
      >
        <el-option
          label="Tous les mois"
          value="all"
        />
        <el-option
          v-for="month in availableMonths"
          :key="month"
          :label="month"
          :value="month"
        />
      </el-select>
    </div>
    <div v-if="loading" class="chart-loading">
      <i class="el-icon-loading" />
      <p>Chargement des données...</p>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-warning" />
      <p>Aucune donnée disponible pour la période sélectionnée</p>
    </div>
    <div v-else ref="chartContainer" class="chart-container">
      <Bar
        :chart-data="chartData"
        :chart-options="chartOptions"
        :height="chartHeight || null"
      />
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { mapGetters } from 'vuex';
// Données fournies par ChartsPanel via provide/inject

// Constantes de conversion
const KG_TO_TONNES = 1000; // Conversion kilogrammes -> tonnes
const TONNES_TO_KG = 1000; // Conversion tonnes -> kilogrammes

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'RecoltesLineChart',
  components: {
    Bar,
  },
  inject: ['dashboardData'],
  props: {
    dataQuantiteCollectee: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      chartData: {
        labels: [],
        datasets: [],
      },
      chartHeight: null,
      selectedMonth: 'all',
      allLabels: [],
      allData: [],
      availableMonths: [],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          title: {
            display: false,
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label(context) {
                const tonnes = context.parsed.y;
                const kg = tonnes * TONNES_TO_KG;
                return `${context.dataset.label}: ${tonnes.toFixed(2)} t (${kg.toFixed(2)} kg)`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Quantité (tonnes)',
            },
            ticks: {
              callback(value) {
                const tonnes = value;
                const kg = tonnes * TONNES_TO_KG;
                return `${tonnes.toFixed(2)} t (${kg.toFixed(0)} kg)`;
              },
            },
          },
          x: {
            title: {
              display: true,
              text: 'Période',
            },
          },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', ['filters']),
    ...mapGetters('camp_agricole', ['camp_id']),
    hasData() {
      if (!this.chartData) {
        return false;
      }
      if (!Array.isArray(this.chartData.labels) || this.chartData.labels.length === 0) {
        return false;
      }
      if (!Array.isArray(this.chartData.datasets) || this.chartData.datasets.length === 0) {
        return false;
      }
      const firstDataset = this.chartData.datasets[0];
      if (!firstDataset || !Array.isArray(firstDataset.data) || firstDataset.data.length === 0) {
        return false;
      }
      return true;
    },
  },
  watch: {
    // Mettre à jour le graphique quand les filtres changent (réutilise les données fournies)
    filters: {
      handler() {
        this.updateFromProvided();
      },
      deep: true,
    },
    camp_id: {
      handler() {
        this.updateFromProvided();
      },
    },
    dataQuantiteCollectee: {
      handler() {
        this.updateFromProvided();
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    // Construire le graphique à partir des données fournies par ChartsPanel
    this.updateFromProvided();
    // Calculer la hauteur après que le DOM soit complètement rendu
    setTimeout(() => {
      this.calculateChartHeight();
    }, 100);
    window.addEventListener('resize', this.calculateChartHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateChartHeight);
  },
  methods: {
    /**
     * Valide que les données fournies sont cohérentes pour l'affichage
     * @param {Object|Array} data - Les données à valider (objet avec labels/data ou tableau)
     * @returns {boolean} - true si les données sont valides
     */
    isValidData(data) {
      if (!data) {
        return false;
      }

      // Si c'est un tableau directement, ce n'est pas valide (il faut labels et data)
      if (Array.isArray(data)) {
        return false;
      }

      // Vérifier que c'est un objet
      if (typeof data !== 'object') {
        return false;
      }

      const labels = Array.isArray(data.labels) ? data.labels : [];
      const rawData = Array.isArray(data.data) ? data.data : [];

      // Vérifier que les tableaux existent et ont au moins un élément
      if (labels.length === 0 || rawData.length === 0) {
        return false;
      }

      return true;
    },

    /**
     * Récupère les données depuis les props ou provide/inject avec validation
     * @returns {Object} - Les données validées ou un objet vide
     */
    getProvidedData() {
      // Priorité à la prop reçue directement; fallback à provide/inject
      let fromProp = null;
      if (this.dataQuantiteCollectee) {
        // Si c'est un tableau, ce n'est pas la bonne structure
        if (Array.isArray(this.dataQuantiteCollectee)) {
          fromProp = null;
        } else if (typeof this.dataQuantiteCollectee === 'object' && Object.keys(this.dataQuantiteCollectee).length > 0) {
          fromProp = this.dataQuantiteCollectee;
        }
      }

      let fromInject = null;
      if (this.dashboardData && this.dashboardData.dataQuantiteCollectee) {
        const injectData = this.dashboardData.dataQuantiteCollectee;
        // Si c'est un tableau, ce n'est pas la bonne structure
        if (Array.isArray(injectData)) {
          fromInject = null;
        } else if (typeof injectData === 'object' && Object.keys(injectData).length > 0) {
          fromInject = injectData;
        }
      }

      // Retourner la première source valide trouvée
      return fromProp || fromInject || {};
    },

    /**
     * Convertit les données de kilogrammes en tonnes
     * @param {Array} rawData - Tableau de valeurs en kilogrammes
     * @returns {Array} - Tableau de valeurs en tonnes
     */
    convertToTonnes(rawData) {
      return rawData.map(v => {
        const num = typeof v === 'string' ? parseFloat(v) : Number(v);
        return isNaN(num) || num < 0 ? 0 : num / KG_TO_TONNES;
      });
    },

    updateFromProvided() {
      this.loading = true;
      try {
        const provided = this.getProvidedData();

        // Si provided est un tableau directement, ce n'est pas la bonne structure
        if (Array.isArray(provided)) {
          this.chartData = { labels: [], datasets: [] };
          this.loading = false;
          return;
        }

        // Validation des données
        if (!this.isValidData(provided)) {
          this.chartData = { labels: [], datasets: [] };
          this.loading = false;
          return;
        }

        const labels = Array.isArray(provided.labels) ? provided.labels : [];
        const rawData = Array.isArray(provided.data) ? provided.data : [];
        const mois = Array.isArray(provided.mois) ? provided.mois : [];

        // Stocker toutes les données brutes
        this.allLabels = labels;
        this.allData = rawData;
        this.availableMonths = mois;

        // Réinitialiser la sélection si le mois sélectionné n'est plus disponible
        if (this.selectedMonth !== 'all' && !this.availableMonths.includes(this.selectedMonth)) {
          this.selectedMonth = 'all';
        }

        // Filtrer les données selon le mois sélectionné
        this.filterDataByMonth();
      } catch (e) {
        console.error('[RecoltesLineChart] Erreur lors de la construction du graphique:', e);
        this.chartData = { labels: [], datasets: [] };
      } finally {
        this.loading = false;
        // Recalculer la hauteur après le chargement des données
        this.$nextTick(() => {
          setTimeout(() => {
            this.calculateChartHeight();
          }, 50);
        });
      }
    },
    /**
     * Extrait le mois depuis une date au format YYYY-MM-DD
     * @param {string} dateStr - Date au format YYYY-MM-DD
     * @returns {string} - Mois au format "Mois YYYY" en français (ex: "Décembre 2025")
     */
    extractMonthFromDate(dateStr) {
      if (!dateStr || typeof dateStr !== 'string') {
        return null;
      }

      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          return null;
        }

        const months = [
          'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
          'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
        ];

        const month = months[date.getMonth()];
        const year = date.getFullYear();

        return `${month} ${year}`;
      } catch (e) {
        return null;
      }
    },

    /**
     * Filtre les données selon le mois sélectionné
     */
    filterDataByMonth() {
      if (this.selectedMonth === 'all' || !this.selectedMonth) {
        // Afficher toutes les données
        this.updateChartData(this.allLabels, this.allData);
        return;
      }

      // Filtrer par mois
      const filteredLabels = [];
      const filteredData = [];

      for (let i = 0; i < this.allLabels.length; i++) {
        const label = this.allLabels[i];
        const monthFromLabel = this.extractMonthFromDate(label);

        if (monthFromLabel === this.selectedMonth) {
          filteredLabels.push(label);
          filteredData.push(this.allData[i]);
        }
      }

      this.updateChartData(filteredLabels, filteredData);
    },

    /**
     * Met à jour les données du graphique
     * @param {Array} labels - Labels à afficher
     * @param {Array} rawData - Données brutes en kg
     */
    updateChartData(labels, rawData) {
      // Ajuster les longueurs si nécessaire (prendre le minimum pour éviter les erreurs)
      const minLength = Math.min(labels.length, rawData.length);
      const adjustedLabels = labels.slice(0, minLength);
      const adjustedRawData = rawData.slice(0, minLength);

      // Les valeurs backend sont en kilogrammes; convertir en tonnes pour coller à l'axe/tooltip
      const tonnesData = this.convertToTonnes(adjustedRawData);

      // Créer un nouvel objet pour forcer la réactivité Vue
      this.chartData = {
        labels: [...adjustedLabels],
        datasets: [
          {
            label: 'Quantité collectée (t)',
            data: [...tonnesData],
            backgroundColor: 'rgba(0, 171, 83, 0.5)',
            borderColor: '#00ab53',
            borderWidth: 1,
          },
        ],
      };
    },

    /**
     * Gère le changement de mois
     */
    onMonthChange() {
      this.filterDataByMonth();
    },

    calculateChartHeight() {
      this.$nextTick(() => {
        setTimeout(() => {
          const parent = this.$el;
          if (!parent) {
            return;
          }

          const header = parent.querySelector('.chart-header');
          const headerHeight = header ? (header.offsetHeight || header.clientHeight || 50) : 50;

          // Utiliser getBoundingClientRect pour une mesure plus précise
          const parentRect = parent.getBoundingClientRect();
          let parentHeight = parentRect.height;

          // Si getBoundingClientRect ne donne pas de résultat, essayer d'autres méthodes
          if (!parentHeight || parentHeight === 0) {
            parentHeight = parent.offsetHeight || parent.clientHeight;
          }

          // Si toujours pas de hauteur, utiliser la hauteur du parent (el-card)
          if (!parentHeight || parentHeight === 0) {
            const cardParent = parent.closest('.el-card__body') || parent.closest('.chart-card');
            if (cardParent) {
              const cardRect = cardParent.getBoundingClientRect();
              parentHeight = cardRect.height || cardParent.offsetHeight || cardParent.clientHeight;
            }
          }

          const availableHeight = parentHeight - headerHeight;

          if (availableHeight > 0) {
            this.chartHeight = Math.max(availableHeight, 350);
          } else if (this.$refs.chartContainer) {
            const containerRect = this.$refs.chartContainer.getBoundingClientRect();
            const containerHeight = containerRect.height || this.$refs.chartContainer.offsetHeight || this.$refs.chartContainer.clientHeight;
            if (containerHeight > 0) {
              this.chartHeight = Math.max(containerHeight, 350);
            } else {
              // Fallback: utiliser une hauteur par défaut basée sur min-height
              this.chartHeight = 350;
            }
          } else {
            // Fallback final
            this.chartHeight = 350;
          }
        }, 150);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.recoltes-line-chart {
  width: 100%;
  height: 100%;
  min-height: 500px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  .chart-header {
    background-color: #00ab53;
    color: #fff;
    padding: 12px 16px;
    border-radius: 4px 4px 0 0;
    flex-shrink: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .chart-title {
      font-size: 16px;
      font-weight: bold;
      display: block;
    }

    .month-select {
      min-width: 200px;

      ::v-deep .el-input__inner {
        background-color: rgba(255, 255, 255, 0.94);
        border-color: rgba(255, 255, 255, 0.3);
        color: #02320a;

        &::placeholder {
          color: rgba(255, 255, 255, 0.7);
        }

        &:hover {
          border-color: rgba(40, 234, 10, 0.292);
        }
      }

      ::v-deep .el-input__suffix {
        .el-select__caret {
          color: #fff;
        }
      }
    }
  }

  .chart-container {
    flex: 1 1 0;
    min-height: 350px;
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    ::v-deep > div {
      width: 100% !important;
      height: 100% !important;
      position: relative;
    }

    ::v-deep canvas {
      width: 100% !important;
      height: 100% !important;
      display: block;
    }

    ::v-deep .chartjs-render-monitor {
      width: 100% !important;
      height: 100% !important;
    }
  }

  .chart-loading,
  .chart-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #909399;

    i {
      font-size: 32px;
      color: #00ab53;
      animation: rotate 1s linear infinite;
    }

    p {
      margin-top: 16px;
      font-size: 14px;
    }
  }

  .chart-empty {
    i {
      color: #e6a23c;
      animation: none;
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

